import { Button } from '@mantine/core'
import { IconAlertCircle, IconArrowRight } from '@tabler/icons-react'
import { ChangeText } from '../components/ChangeText'
import { InfoIcon } from '../components/InfoIcon'
import { SectionCard } from '../components/SectionCard'
import { formatCount, formatDayCount, formatDays } from '../format'
import type { usePopulationProviders } from '../hooks'

/** A provider whose median time to resolve is at least this share above the population's is called out. */
const CALL_OUT_SHARE = 0.1

/** A bar's width, as a share of the slowest provider's median (or the population's, if longer). */
const barPercent = (days: number | null, scale: number) =>
  days === null || scale <= 0 ? 0 : Math.min(100, Math.round((days / scale) * 100))

/**
 * Attributed providers ranked by median time to resolve (fastest first), drawn as bars against
 * the population's own median. Choosing one sets the page's Provider filter, so every section
 * and the drawer narrow to that provider's panel.
 */
export function ProviderPerformance({
  providers,
  populationMedianDays,
  selectedProviderId,
  onSelectProvider,
}: {
  providers: ReturnType<typeof usePopulationProviders>
  /** The median time to resolve of everyone the page's filters select, for the marker. */
  populationMedianDays: number | null | undefined
  selectedProviderId: string | undefined
  onSelectProvider: (providerId: string | null) => void
}) {
  const data = providers.data
  const list = data?.providers ?? []
  const days = (p: (typeof list)[number]) => p.closureTime.current.medianDays
  // Compare only when both sides are current: the list may still be the previous filters'.
  const median = providers.isPlaceholderData ? null : (populationMedianDays ?? null)
  const slowest = list
    .filter((p) => days(p) !== null)
    .reduce<
      (typeof list)[number] | undefined
    >((slow, p) => (!slow || days(p)! > days(slow)! ? p : slow), undefined)
  const scale = Math.max(slowest ? days(slowest)! : 0, median ?? 0)
  // In whole days, as both medians are shown, so the callout's sentence adds up (PD-1942).
  const gapDays = slowest && median !== null ? Math.round(days(slowest)!) - Math.round(median) : 0
  const callOut =
    !selectedProviderId &&
    slowest &&
    median !== null &&
    gapDays > 0 &&
    days(slowest)! >= median * (1 + CALL_OUT_SHARE)
      ? slowest
      : undefined

  return (
    <SectionCard
      title="Which providers need attention?"
      subtitle="Median time to resolve by attributed provider"
      definition={data?.definitions.closureTime}
      query={providers}
      isEmpty={list.length === 0 && !data?.unattributedPatients}
      emptyText="No attributed providers for the selected filters."
      actions={
        selectedProviderId && (
          <Button variant="subtle" size="compact-sm" onClick={() => onSelectProvider(null)}>
            All providers
          </Button>
        )
      }
    >
      {data && (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            {median !== null && (
              <span className="inline-flex items-center gap-1.5 font-medium text-slate-700">
                <span className="w-5 border-t-2 border-dashed border-slate-400" aria-hidden />
                Population median: {formatDays(median)}
              </span>
            )}
            <span className="inline-flex items-center">
              Panel
              <InfoIcon label="Panel" definition={data.definitions.panelPatients} />
            </span>
            <span className="inline-flex items-center">
              Open gaps
              <InfoIcon label="Open gaps" definition={data.definitions.openCareGaps} />
            </span>
          </div>
          <ul className="flex flex-col gap-1">
            {list.map((p) => {
              const selected = p.providerId === selectedProviderId
              const value = days(p)
              const slower = value !== null && median !== null && value > median
              return (
                <li key={p.providerId}>
                  <button
                    type="button"
                    aria-pressed={selected}
                    aria-label={`Filter to ${p.providerName ?? p.providerId}`}
                    onClick={() => onSelectProvider(selected ? null : p.providerId)}
                    className={`grid w-full cursor-pointer grid-cols-[minmax(0,14rem)_1fr_4.5rem] items-center gap-3 rounded-md px-2 py-1.5 text-left hover:bg-slate-50 ${selected ? 'bg-blue-50' : ''}`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-slate-900">
                        {p.providerName ?? p.providerId}
                      </span>
                      <span className="flex flex-wrap items-center gap-x-2 text-[11px] text-slate-500">
                        <span className="tabular-nums">
                          {formatCount(p.panelPatients)} panel · {formatCount(p.openCareGaps)} gaps
                        </span>
                        <ChangeText
                          value={p.closureTime.changeDays}
                          unit="days"
                          better="down"
                          label=""
                        />
                      </span>
                    </span>
                    <span className="relative h-2.5 rounded-full bg-slate-100">
                      <span
                        className={`absolute inset-y-0 left-0 rounded-full ${slower ? 'bg-red-500' : 'bg-blue-500'}`}
                        style={{ width: `${barPercent(value, scale)}%` }}
                      />
                      {median !== null && (
                        <span
                          className="absolute -inset-y-1 border-l-2 border-dashed border-slate-400"
                          style={{ left: `${barPercent(median, scale)}%` }}
                          aria-hidden
                        />
                      )}
                    </span>
                    <span className="text-right text-sm font-semibold text-slate-900 tabular-nums">
                      {formatDays(value)}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
          {callOut && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50/70 p-3 text-sm">
              <div className="flex min-w-0 items-start gap-2">
                <IconAlertCircle size={18} className="mt-0.5 shrink-0 text-amber-600" aria-hidden />
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900">Slower than the population</div>
                  <div className="text-slate-700">
                    {callOut.providerName ?? callOut.providerId}’s panel takes{' '}
                    {formatDayCount(gapDays)} longer to resolve a gap ({formatDays(days(callOut))}{' '}
                    against a population median of {formatDays(median)}
                    ).
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onSelectProvider(callOut.providerId)}
                className="inline-flex shrink-0 cursor-pointer items-center gap-1 text-sm font-semibold text-amber-700 hover:underline"
              >
                Focus on this panel
                <IconArrowRight size={14} aria-hidden />
              </button>
            </div>
          )}
          <p className="mt-3 text-xs text-slate-500">
            {formatCount(data.unattributedPatients)} patients with no attributed provider.
          </p>
        </>
      )}
    </SectionCard>
  )
}
